/**
 * Workshop version of ConnectAppFrame.
 *
 * Agent-initiated single-app connection prompt. Shown when the agent
 * needs a specific app connected to continue a task.
 */
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";
import { FrameFooter } from "@/workshop/components/shapes/shared/frame-footer";
import { APP_BG, APP_ICON, APP_NAME } from "./app-icons";
import type { AppId } from "./app-icons";

interface ConnectAppFrameProps {
  appId?: AppId;
  reason?: string;
  agentName?: string;
  onConnect?: (appId: AppId) => void;
  onSkip?: () => void;
  onResizeStart?: React.MouseEventHandler;
  className?: string;
  "data-testid"?: string;
}

export function ConnectAppFrame({
  appId = "gmail",
  reason = "I need access to continue this task.",
  agentName = "System",
  onConnect,
  onSkip,
  onResizeStart,
  className,
  "data-testid": testId = "connect-app-frame",
}: ConnectAppFrameProps) {
  const Icon = APP_ICON[appId];
  const bg = APP_BG[appId];
  const name = APP_NAME[appId];

  return (
    <div
      className={cn(
        "denker-glass-frame flex flex-col rounded-frame overflow-hidden",
        className,
      )}
      data-testid={testId}
    >
      <FrameHeader
        title="Connect App"
        icon={Icons.Plug}
        accentColor="bg-frame-connection"
        agentName={agentName}
        statusColor="bg-status-creating"
        data-testid={`${testId}-header`}
      />

      <div className="flex flex-col items-center gap-3 px-4 py-5 text-center">
        {/* App icon */}
        <div
          className={cn(
            "flex h-12 w-12 items-center justify-center rounded-[13px] shadow-md",
            bg,
          )}
          data-testid={`${testId}-icon`}
        >
          <Icon size={28} />
        </div>

        {/* App name + status */}
        <div>
          <p className="text-canvas-md font-bold text-primary">Connect {name}</p>
          <p className="text-canvas-sm text-muted">Not connected</p>
        </div>

        {/* Agent reason */}
        <div
          className="w-full rounded-lg border border-overlay-border bg-surface px-3 py-2"
          data-testid={`${testId}-reason`}
        >
          <p className="text-canvas-xs italic text-muted leading-relaxed">
            &ldquo;{reason}&rdquo;
          </p>
        </div>

        {/* Connect CTA */}
        <Button
          type="button"
          onClick={() => onConnect?.(appId)}
          size="lg"
          className="w-full"
          data-testid={`${testId}-connect-btn`}
        >
          Connect {name}
          <Icons.ArrowRight className="h-appkit-sm w-appkit-sm" />
        </Button>

        {/* Skip */}
        <Button
          type="button"
          onClick={onSkip}
          variant="ghost"
          size="sm"
          data-testid={`${testId}-skip-btn`}
        >
          Skip for now
        </Button>
      </div>

      <FrameFooter
        onResizeStart={onResizeStart}
        data-testid={`${testId}-footer`}
      />
    </div>
  );
}

export type { ConnectAppFrameProps };
